import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faChartLine,
  faCar,
  faCalendarAlt,
  faHistory,
  faChartBar,
  faSignOutAlt,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../auth';
import { useNavigate } from 'react-router-dom';
import { drivers, constructors } from '../data/teamData';
import { fantasyTeamService, pointsService } from '../services/firestore';
import f1Calendar2025 from '../data/f1-calendar-2025.json';

const constructorBackgrounds = {
  'Red Bull Racing': '/images/redbull.png',
  'Mercedes': '/images/mercedes.png',
  'Ferrari': '/images/ferrari.png',
  'McLaren': '/images/mclaren.png',
  'Aston Martin': '/images/astonmartin.png',
  'Alpine': '/images/alpine.png',
  'Williams': '/images/williams.png',
  'AlphaTauri': '/images/visacashapp.png',
  'Alfa Romeo': '/images/kicksauber.png',
  'Haas F1 Team': '/images/haas.png',
  // Team selection IDs
  'red_bull': '/images/redbull.png',
  'ferrari': '/images/ferrari.png',
  'mercedes': '/images/mercedes.png',
  'mclaren': '/images/mclaren.png',
  'aston_martin': '/images/astonmartin.png',
  'alpine': '/images/alpine.png',
  'williams': '/images/williams.png',
  'rb': '/images/visacashapp.png',
  'kick_sauber': '/images/kicksauber.png',
  'haas': '/images/haas.png'
};

const teamNames = {
  'red_bull': 'Red Bull Racing',
  'ferrari': 'Scuderia Ferrari',
  'mercedes': 'Mercedes-AMG',
  'mclaren': 'McLaren',
  'aston_martin': 'Aston Martin',
  'alpine': 'Alpine',
  'williams': 'Williams',
  'rb': 'RB',
  'kick_sauber': 'Kick Sauber',
  'haas': 'Haas F1 Team'
};

const teamColors = {
  'red_bull': '#0600EF',
  'ferrari': '#E8002D',
  'mercedes': '#00D2BE',
  'mclaren': '#FF8700',
  'aston_martin': '#006F62',
  'alpine': '#0090FF',
  'williams': '#00A0DC',
  'rb': '#6692FF',
  'kick_sauber': '#52E252',
  'haas': '#B6BABD'
};

const Dashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [userTeam, setUserTeam] = useState(null);
  const [nextRace, setNextRace] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [leagueStandings, setLeagueStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamPoints, setTeamPoints] = useState(0);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [favoriteTeam, setFavoriteTeam] = useState(null);
  const [teamColor, setTeamColor] = useState('#E10600');

  // Helper function to get driver name by ID
  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : driverId;
  };

  // Helper function to get constructor name by ID
  const getConstructorName = (constructorId) => {
    const constructor = constructors.find(c => c.id === constructorId);
    return constructor ? constructor.name : constructorId;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user's team data from Firestore
        const teamData = await fantasyTeamService.getTeam(user.uid);
        setUserTeam(teamData);
        
        // Set background image and team info - prioritize favorite team from profile
        const selectedTeam = user.firestoreUser?.favoriteTeam;
        console.log('🏎️ Dashboard Debug:', {
          selectedTeam,
          favoriteTeamName: user.firestoreUser?.favoriteTeamName,
          allUserData: user.firestoreUser
        });
        
        if (selectedTeam) {
          const bgImage = constructorBackgrounds[selectedTeam];
          const teamName = teamNames[selectedTeam] || selectedTeam;
          const color = teamColors[selectedTeam] || '#E10600';
          
          console.log('✅ Setting team:', { teamName, color, bgImage });
          
          setBackgroundImage(bgImage || '');
          setFavoriteTeam(teamName);
          setTeamColor(color);
        } else if (user.firestoreUser?.favoriteTeamImage) {
          console.log('📸 Using favorite team image');
          setBackgroundImage(user.firestoreUser.favoriteTeamImage);
        } else if (teamData?.constructor) {
          console.log('🏁 Fallback to fantasy team constructor');
          // Fallback to fantasy team constructor
          setBackgroundImage(constructorBackgrounds[teamData.constructor] || '');
        } else {
          console.log('⚠️ No team data found');
        }

        // Fetch team points from Firestore
        const pointsData = await pointsService.getUserPoints(user.uid);
        setTeamPoints(pointsData.totalPoints || 0);

        // Use local 2025 calendar - find next race
        const today = new Date();
        const upcomingRace = f1Calendar2025.find(race => new Date(race.date) > today);
        
        if (upcomingRace) {
          // Transform to match expected format
          setNextRace({
            raceName: upcomingRace.grand_prix,
            date: upcomingRace.date,
            Circuit: {
              circuitName: upcomingRace.circuit,
              Location: {
                locality: upcomingRace.circuit.split(' ')[0],
                country: upcomingRace.circuit.split(' ').slice(-1)[0]
              }
            }
          });
        }

        // Mock data for league standings (will be replaced with real data later)
        setLeagueStandings([
          { position: 1, name: "Max's Fans", points: 325 },
          { position: 2, name: "Ferrari Forever", points: 280 },
          { position: 3, name: "McLaren Maniacs", points: 245 },
        ]);

        // Mock recent results (will be replaced with real data later)
        setRecentResults([
          { race: "Bahrain GP", points: 49, position: 2 },
          { race: "Saudi Arabian GP", points: 42, position: 3 },
          { race: "Australian GP", points: 35, position: 5 },
        ]);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div 
      className="dashboard-container"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        '--team-color': teamColor
      }}
    >
      {/* Top Navigation Bar */}
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="dashboard-title">F1 Fantasy</h1>
        </div>
        <div className="nav-right">
          <div className="total-points" style={{ borderColor: teamColor }}>
            <FontAwesomeIcon icon={faTrophy} className="points-icon" style={{ color: teamColor }} />
            <span style={{ color: teamColor }}>{teamPoints}</span>
            <span className="points-label">Points</span>
          </div>
          <button className="logout-btn" onClick={handleLogout} style={{ borderColor: teamColor, color: teamColor }}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            Logout
          </button>
        </div>
      </nav>

      {/* Hero Section with Team Name */}
      {favoriteTeam && (
        <div className="hero-section">
          <div className="hero-content">
            <h2 className="hero-greeting">Welcome back, {user?.displayName || user?.email?.split('@')[0] || 'Racer'}</h2>
            <div className="team-banner" style={{ borderLeftColor: teamColor }}>
              <FontAwesomeIcon icon={faCar} className="team-icon" style={{ color: teamColor }} />
              <div className="team-info">
                <span className="team-label">Your Team</span>
                <h1 className="team-name" style={{ color: teamColor }}>{favoriteTeam}</h1>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Team Overview Card */}
        <div className="dashboard-card team-card">
          <div className="card-header">
            <div className="card-title-group">
              <FontAwesomeIcon icon={faCar} className="card-icon" style={{ color: teamColor }} />
              <h3>Fantasy Team</h3>
            </div>
            <button 
              className="edit-team-btn" 
              onClick={() => navigate('/edit-team')}
              style={{ borderColor: teamColor, color: teamColor }}
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit
            </button>
          </div>
          <div className="card-content">
            {userTeam ? (
              <>
                <div className="driver-list">
                  <div className="section-header">
                    <span className="emoji">🏎️</span>
                    <h4>Drivers</h4>
                  </div>
                  {[1, 2, 3, 4].map((num) => (
                    <div key={num} className="driver-item">
                      <span className="driver-number" style={{ color: teamColor }}>#{num}</span>
                      <span className="driver-name">{getDriverName(userTeam[`driver_${num}`])}</span>
                    </div>
                  ))}
                </div>
                <div className="constructor">
                  <div className="section-header">
                    <span className="emoji">🏁</span>
                    <h4>Constructor</h4>
                  </div>
                  <span className="constructor-name">{getConstructorName(userTeam.constructor)}</span>
                </div>
                <div className="budget">
                  <div className="section-header">
                    <span className="emoji">💰</span>
                    <h4>Budget Remaining</h4>
                  </div>
                  <span className="budget-amount" style={{ color: teamColor }}>
                    ${userTeam.budgetRemaining || userTeam.budget_remaining}M
                  </span>
                </div>
              </>
            ) : (
              <div className="loading-card">
                <div className="spinner"></div>
                <p>Loading team data...</p>
              </div>
            )}
          </div>
        </div>

        {/* Next Race Card */}
        <div className="dashboard-card next-race-card">
          <div className="card-header">
            <div className="card-title-group">
              <FontAwesomeIcon icon={faCalendarAlt} className="card-icon" style={{ color: teamColor }} />
              <h3>Next Race</h3>
            </div>
            <button 
              className="predict-btn"
              onClick={() => navigate('/predict-race')}
              style={{ backgroundColor: teamColor }}
            >
              Predict
            </button>
          </div>
          <div className="card-content">
            {nextRace ? (
              <>
                <h2 className="race-name" style={{ color: teamColor }}>{nextRace.raceName}</h2>
                <div className="race-details">
                  <div className="race-info-item">
                    <FontAwesomeIcon icon={faCalendarAlt} className="info-icon" />
                    <p className="race-date">
                      {new Date(nextRace.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="race-info-item">
                    <FontAwesomeIcon icon={faCar} className="info-icon" />
                    <p className="race-circuit">{nextRace.Circuit.circuitName}</p>
                  </div>
                  <div className="race-info-item">
                    <span className="emoji">📍</span>
                    <p className="race-location">
                      {nextRace.Circuit.Location.locality}, {nextRace.Circuit.Location.country}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="loading-card">
                <div className="spinner"></div>
                <p>Loading next race...</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Results Card */}
        <div className="dashboard-card results-card">
          <div className="card-header">
            <div className="card-title-group">
              <FontAwesomeIcon icon={faHistory} className="card-icon" style={{ color: teamColor }} />
              <h3>Recent Results</h3>
            </div>
            <button 
              className="view-all-btn"
              onClick={() => navigate('/race-results')}
              style={{ color: teamColor }}
            >
              View All →
            </button>
          </div>
          <div className="card-content">
            <div className="results-list">
              {recentResults.map((result, index) => (
                <div key={index} className="result-item">
                  <div className="result-left">
                    <span className="result-position" style={{ 
                      backgroundColor: index === 0 ? teamColor : 'rgba(255, 255, 255, 0.1)',
                      color: index === 0 ? '#fff' : '#ccc'
                    }}>
                      P{result.position}
                    </span>
                    <span className="result-race-name">{result.race}</span>
                  </div>
                  <span className="result-points" style={{ color: teamColor }}>
                    +{result.points}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* League Standings Card */}
        <div className="dashboard-card standings-card">
          <div className="card-header">
            <div className="card-title-group">
              <FontAwesomeIcon icon={faChartBar} className="card-icon" style={{ color: teamColor }} />
              <h3>League Standings</h3>
            </div>
            <button 
              className="view-all-btn"
              onClick={() => navigate('/leagues')}
              style={{ color: teamColor }}
            >
              View All →
            </button>
          </div>
          <div className="card-content">
            <div className="standings-list">
              {leagueStandings.map((standing, index) => (
                <div key={index} className="standing-item">
                  <div className="standing-position" style={{ 
                    backgroundColor: index === 0 ? teamColor : 'rgba(255, 255, 255, 0.1)',
                    color: index === 0 ? '#fff' : '#ccc'
                  }}>
                    {standing.position}
                  </div>
                  <span className="standing-name">{standing.name}</span>
                  <span className="standing-points">{standing.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-actions">
        <button 
          className="action-card" 
          onClick={() => navigate('/predict-race')}
          style={{ borderColor: teamColor }}
        >
          <FontAwesomeIcon icon={faChartLine} className="action-icon" style={{ color: teamColor }} />
          <div className="action-text">
            <h4>Make Predictions</h4>
            <p>Predict the next race results</p>
          </div>
        </button>
        <button 
          className="action-card" 
          onClick={() => navigate('/race-results')}
          style={{ borderColor: teamColor }}
        >
          <FontAwesomeIcon icon={faHistory} className="action-icon" style={{ color: teamColor }} />
          <div className="action-text">
            <h4>Race Results</h4>
            <p>View all race results</p>
          </div>
        </button>
        <button 
          className="action-card" 
          onClick={() => navigate('/leagues')}
          style={{ borderColor: teamColor }}
        >
          <FontAwesomeIcon icon={faTrophy} className="action-icon" style={{ color: teamColor }} />
          <div className="action-text">
            <h4>My Leagues</h4>
            <p>Join and manage leagues</p>
          </div>
        </button>
        <button 
          className="action-card" 
          onClick={() => navigate('/edit-team')}
          style={{ borderColor: teamColor }}
        >
          <FontAwesomeIcon icon={faEdit} className="action-icon" style={{ color: teamColor }} />
          <div className="action-text">
            <h4>Edit Team</h4>
            <p>Update your fantasy team</p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Dashboard; 